import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSetupGuide } from "@/hooks/use-setup-guide";
import {
  GUIDED_TOUR_STEPS,
  TOUR_STEP_COUNT,
  tourRefsFrom,
  type TourRefs,
  type TourStepConfig,
} from "@/lib/guided-tour-config";

/**
 * Tour event bus — imported from @/lib/tour-events and re-exported here so
 * pages that already import emitTourEvent / TourEventPayload from this hook
 * don't need to change their import paths.
 */
import {
  emitTourEvent as _emitTourEvent,
  subscribeTourEvents as _subscribeTourEvents,
  type TourEventPayload as _TourEventPayload,
  type TourListener,
} from "@/lib/tour-events";

export type TourEventPayload = _TourEventPayload;
export const emitTourEvent = _emitTourEvent;
export const subscribeTourEvents = _subscribeTourEvents;

export interface GuidedTourController {
  /** True while the guided tour should be driving the UI. */
  active: boolean;
  /** Zero-based index of the current step. */
  index: number;
  total: number;
  /** Current step config, or null when finished / inactive. */
  step: TourStepConfig | null;
  /** True once the user has advanced past the last step (show completion). */
  finished: boolean;
  refs: TourRefs;
  /**
   * True when an advanceOn event has fired but the overlay is waiting for
   * the user to click "Continue" before persisting the next step.
   */
  pendingAdvance: boolean;
  /**
   * How many times the current step's advanceOn event has fired so far.
   * Useful for displaying progress on minEventCount steps ("1 of 2").
   */
  stepEventCount: number;
  /** Call this when the user confirms the step-complete notification. */
  confirmAdvance: () => void;
  next: () => void;
  prev: () => void;
  /** Bail out of the tour. The caller decides whether to clear demo data. */
  exit: (clearDemoData: boolean) => Promise<void>;
  /** Finish the tour. The caller decides whether to clear demo data. */
  complete: (clearDemoData: boolean) => Promise<void>;
}

/**
 * Pages call `useGuidedTour()` to read the current step. Only the overlay
 * passes `{ drive: true }`: it alone listens for page events and owns the
 * "Step complete → Continue" state. When every caller listened, whichever
 * subscribed first took the event, and if that was a page the overlay never
 * learned the step was done, leaving the user stuck on it.
 */
export function useGuidedTour({ drive = false }: { drive?: boolean } = {}): GuidedTourController {
  const { guideState, setTourStep, setTourRef, endTour } = useSetupGuide();

  const active = !!guideState?.tourActive;

  const index = guideState?.tourStep ?? 0;
  const finished = active && index >= TOUR_STEP_COUNT;
  const step = active && index < TOUR_STEP_COUNT ? GUIDED_TOUR_STEPS[index] : null;

  const refs: TourRefs = useMemo(
    () => tourRefsFrom(guideState),
    [guideState?.demoData, guideState?.latestQuoteId, guideState?.latestInvoiceId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Guard against double-advancing while a PATCH is in flight.
  const advancingRef = useRef(false);
  const [, force] = useState(0);

  // Count how many times the current step's advanceOn event has fired.
  // Resets when the step changes. Used for minEventCount gating and progress display.
  const stepEventCountRef = useRef(0);
  const [stepEventCount, setStepEventCount] = useState(0);

  // Pending advance: an advanceOn event fired, but the overlay waits for user
  // confirmation ("✓ Step complete! Continue →") before calling setTourStep.
  const [pendingAdvance, setPendingAdvance] = useState(false);
  const pendingPayloadRef = useRef<TourEventPayload | undefined>(undefined);

  // Reset pendingAdvance and event counter whenever the step changes.
  useEffect(() => {
    setPendingAdvance(false);
    pendingPayloadRef.current = undefined;
    stepEventCountRef.current = 0;
    setStepEventCount(0);
  }, [index]);

  const next = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setPendingAdvance(false);
    void setTourStep(index + 1).finally(() => {
      advancingRef.current = false;
      force((n) => n + 1);
    });
  }, [index, setTourStep]);

  const prev = useCallback(() => {
    if (advancingRef.current || index <= 0) return;
    advancingRef.current = true;
    setPendingAdvance(false);
    void setTourStep(index - 1).finally(() => {
      advancingRef.current = false;
      force((n) => n + 1);
    });
  }, [index, setTourStep]);

  const confirmAdvance = useCallback(() => {
    if (advancingRef.current) return;
    const payload = pendingPayloadRef.current;
    advancingRef.current = true;
    setPendingAdvance(false);
    const persist =
      payload && (payload.quoteId != null || payload.invoiceId != null)
        ? setTourRef(payload)
        : Promise.resolve();
    void persist.then(() => {
      void setTourStep(index + 1).finally(() => {
        advancingRef.current = false;
        force((n) => n + 1);
      });
    });
  }, [index, setTourStep, setTourRef]);

  const exit = useCallback(
    (clearDemoData: boolean) => endTour({ clearDemoData }),
    [endTour],
  );

  const complete = useCallback(
    (clearDemoData: boolean) => endTour({ clearDemoData }),
    [endTour],
  );

  // Listen for page-emitted advance events for the active step.
  useEffect(() => {
    if (!drive || !active || !step?.advanceOn) return;
    const advanceOn = step.advanceOn;
    const skipConfirmation = step.skipConfirmation ?? false;
    const minEventCount = step.minEventCount ?? 1;
    const unsub = subscribeTourEvents((name, payload) => {
      if (name !== advanceOn) return;
      if (advancingRef.current) return;

      // Increment the per-step event counter. Gate advance until minEventCount.
      stepEventCountRef.current += 1;
      setStepEventCount(stepEventCountRef.current);
      if (stepEventCountRef.current < minEventCount) return;

      if (skipConfirmation) {
        // Auto-advance immediately — no confirmation card needed.
        advancingRef.current = true;
        const persist =
          payload && (payload.quoteId != null || payload.invoiceId != null)
            ? setTourRef(payload)
            : Promise.resolve();
        void persist.then(() => {
          void setTourStep(index + 1).finally(() => {
            advancingRef.current = false;
            force((n) => n + 1);
          });
        });
        return;
      }
      // Store payload for when the user confirms.
      pendingPayloadRef.current = payload;
      setPendingAdvance(true);
    });
    return unsub;
  }, [drive, active, step?.advanceOn, step?.skipConfirmation, step?.minEventCount, index]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    active, index, total: TOUR_STEP_COUNT, step, finished, refs,
    pendingAdvance, stepEventCount, confirmAdvance,
    next, prev, exit, complete,
  };
}
